/**
 * VortexPrefetch - After HTML is rewritten, proactively fetch and cache
 * all linked scripts, styles, and key resources before the browser asks.
 *
 * This eliminates the waterfall delay where the browser has to:
 * 1. Parse HTML
 * 2. Discover a script tag
 * 3. Request it
 * 4. Wait for the SW to rewrite it
 *
 * Instead we do steps 3+4 in parallel with step 1.
 */

import { rewriteUrl } from "@rewriters/url";
import { URLMeta } from "@rewriters/url";

// URLs we've already prefetched this session
const prefetchedUrls = new Set<string>();

// Limit concurrent prefetch requests
const MAX_CONCURRENT = 4;
let activePrefetches = 0;
const prefetchQueue: Array<() => void> = [];

function runNext() {
	if (prefetchQueue.length > 0 && activePrefetches < MAX_CONCURRENT) {
		const next = prefetchQueue.shift()!;
		next();
	}
}

async function prefetchOne(rewrittenUrl: string): Promise<void> {
	if (prefetchedUrls.has(rewrittenUrl)) return;
	prefetchedUrls.add(rewrittenUrl);

	await new Promise<void>((resolve) => {
		const run = async () => {
			activePrefetches++;
			try {
				// Fetch through the SW (which will rewrite + cache it)
				await fetch(rewrittenUrl, { priority: "low" } as RequestInit);
			} catch {
				// ignore prefetch errors
			} finally {
				activePrefetches--;
				runNext();
				resolve();
			}
		};

		if (activePrefetches < MAX_CONCURRENT) {
			run();
		} else {
			prefetchQueue.push(run);
		}
	});
}

// Extract prefetchable URLs from rewritten HTML
export function extractPrefetchUrls(html: string): string[] {
	const urls: string[] = [];

	// Match script src
	const scriptRe = /<script[^>]+src=["']([^"']+)["']/gi;
	let m: RegExpExecArray | null;
	while ((m = scriptRe.exec(html)) !== null) {
		urls.push(m[1]);
	}

	// Match link stylesheet href
	const linkRe =
		/<link[^>]+rel=["']stylesheet["'][^>]+href=["']([^"']+)["']/gi;
	while ((m = linkRe.exec(html)) !== null) {
		urls.push(m[1]);
	}

	// Match link preload
	const preloadRe = /<link[^>]+rel=["']preload["'][^>]+href=["']([^"']+)["']/gi;
	while ((m = preloadRe.exec(html)) !== null) {
		urls.push(m[1]);
	}

	return urls.filter(
		(u) => u.startsWith("/") || u.startsWith("http://") || u.startsWith("https://")
	);
}

// Kick off background prefetching after HTML rewrite
export function schedulePrefetch(
	rewrittenHtml: string,
	_meta: URLMeta
): void {
	// Run async, don't block the response
	setTimeout(async () => {
		const urls = extractPrefetchUrls(rewrittenHtml);
		// Only prefetch URLs that are already rewritten (go through SW scope)
		const swUrls = urls.filter((u) => u.includes("/vortex/") || u.startsWith("/scram"));

		for (const url of swUrls.slice(0, 20)) {
			// max 20 prefetches per page
			prefetchOne(url);
		}
	}, 0);
}
